import type { ActivityOf } from '@wyn/content';
import { assertGame } from '../errors.ts';
import { createRng } from '../rng.ts';
import { clamp, type MinigameResult, type TurnEngineImpl } from './types.ts';

export const CARDS_MAX_STREAK = 8;
export const SUITS = ['♠', '♥', '♦', '♣'] as const;

export interface Card {
  /** 2–14; 11–14 are J, Q, K, A. */
  rank: number;
  suit: (typeof SUITS)[number];
}

/** Private: the remaining deck (top is last) never leaves the server until the reveal. */
export interface CardsState {
  deck: Card[];
  current: Card;
  pot: number;
  suspicion: number;
  streak: number;
  /** The card just turned over by a guess. */
  last: Card | null;
  /** The next card, glimpsed by a peek. Shown only in the view straight after the peek. */
  peeked: Card | null;
  say: string;
  over: 'cash' | 'caught' | 'lose' | null;
}

export type CardsMove = { type: 'higher' } | { type: 'lower' } | { type: 'peek' } | { type: 'cash' };

export interface CardsView {
  current: Card;
  last: Card | null;
  peeked: Card | null;
  pot: number;
  stake: number;
  streak: number;
  maxStreak: number;
  suspicion: number;
  /** Pot multiplier for a right guess (a flat `CARDS_PEEK_PAYS` straight after a peek); 0 when that guess is impossible. */
  higher: number;
  lower: number;
  cardsLeft: number;
  say: string;
  over: CardsState['over'];
}

const odds = (s: CardsState) => {
  const n = s.deck.length;
  if (!n) return { higher: 0, lower: 0 };
  return { higher: s.deck.filter((c) => c.rank > s.current.rank).length / n, lower: s.deck.filter((c) => c.rank < s.current.rank).length / n };
};
/** What a right guess pays, as a share of fair odds: the house's cut on every card. */
export const CARDS_ODDS = 0.91;
/**
 * The pot multiplier for a right guess with chance `p`, when the other side comes up with chance `q`
 * (a push, an equal rank, is the rest). The 1.05 floor alone would make a near-sure guess pay more
 * than it risks (with a 2 showing, "higher" can only win or push), so the floor is capped at what
 * keeps the house's cut on the chance of losing. A sure thing pays 1: the pot doesn't grow.
 */
const mult = (p: number, q: number) => (p > 0 ? Math.min(clamp(CARDS_ODDS / p, 1.05, 4), 1 + (CARDS_ODDS * q) / p) : 0);
const pays = (s: CardsState, side: 'higher' | 'lower') => {
  const o = odds(s);
  return side === 'higher' ? mult(o.higher, o.lower) : mult(o.lower, o.higher);
};
/**
 * A guess made on a peeked card is a sure thing, so it pays a flat sliver rather than the odds.
 * Cheating protects a pot; it doesn't build one. With honest odds at `CARDS_ODDS`, the house keeps its edge.
 */
export const CARDS_PEEK_PAYS = 1.1;
const NAMES: Record<number, string> = { 11: 'J', 12: 'Q', 13: 'K', 14: 'A' };
export const cardName = (c: Card) => `${NAMES[c.rank] ?? c.rank}${c.suit}`;
const sure = (s: CardsState) => s.peeked !== null;

/**
 * The Black Dog: high-low with a marked deck. Each right guess grows the pot by the odds; peeking at
 * your marks shows the next card but raises suspicion, and getting caught loses everything. The deck
 * is shuffled from the server seed when you sit down, so the commitment covers every card.
 * Ported from the Harrowmere prototype (the Gilded Rook).
 */
export const cards: TurnEngineImpl<ActivityOf<'cards'>, CardsState, CardsMove, CardsView> = {
  init(a, _level, serverSeed) {
    const deck = createRng(serverSeed).shuffle(Array.from({ length: 52 }, (_, i): Card => ({ rank: 2 + (i % 13), suit: SUITS[Math.floor(i / 13)]! })));
    const current = deck.pop()!;
    return { deck, current, pot: a.stake, suspicion: 0, streak: 0, last: null, peeked: null, say: 'Higher or lower?', over: null };
  },

  move(a, prev, m, { rng, level }) {
    assertGame(!prev.over, 'INVALID', 'The hand is over.');
    const s: CardsState = { ...prev, deck: [...prev.deck], peeked: null };
    if (m.type === 'cash') {
      s.over = 'cash';
      s.say = `You scoop up ${s.pot} coin.`;
      return s;
    }
    if (m.type === 'peek') {
      s.suspicion += Math.max(8, a.heat * (0.8 + rng.next() * 0.4) - level * 0.5);
      // Every peek risks the room: the hotter it is, the likelier someone sees.
      if (s.suspicion >= 100 || rng.next() < s.suspicion / 100) {
        s.over = 'caught';
        s.say = 'A burly patron spots your marks!';
        return s;
      }
      s.peeked = s.deck[s.deck.length - 1]!;
      s.say = s.suspicion > 70 ? 'Eyes are on you…' : 'You glimpse the mark.';
      return s;
    }
    const o = odds(s);
    const p = m.type === 'higher' ? o.higher : o.lower;
    assertGame(p > 0, 'INVALID', `No card can be ${m.type} than that.`);
    const rate = sure(prev) ? CARDS_PEEK_PAYS : pays(s, m.type);
    const next = s.deck.pop()!;
    s.last = next;
    s.suspicion = Math.max(0, s.suspicion - 6);
    const right = m.type === 'higher' ? next.rank > s.current.rank : next.rank < s.current.rank;
    if (next.rank === s.current.rank) s.say = `${cardName(next)}: a push.`;
    else if (right) {
      const pot = Math.round(s.pot * rate);
      // Only a guess that grew the pot counts towards the streak, so a sure thing can't run the table.
      if (pot > s.pot) s.streak++;
      s.say = pot > s.pot ? `${cardName(next)}: right!` : `${cardName(next)}: right, but a sure thing pays nothing.`;
      s.pot = pot;
    } else {
      s.current = next;
      s.over = 'lose';
      s.say = `${cardName(next)}: wrong!`;
      return s;
    }
    s.current = next;
    if (s.streak >= CARDS_MAX_STREAK || s.deck.length === 0) {
      s.over = 'cash';
      s.say += ` The table is yours: ${s.pot} coin.`;
    }
    return s;
  },

  view(a, s) {
    const o = odds(s);
    return {
      current: s.current,
      last: s.last,
      peeked: s.peeked,
      pot: s.pot,
      stake: a.stake,
      streak: s.streak,
      maxStreak: CARDS_MAX_STREAK,
      suspicion: Math.min(100, Math.round(s.suspicion * 10) / 10),
      higher: o.higher ? Math.round((s.peeked ? CARDS_PEEK_PAYS : pays(s, 'higher')) * 100) / 100 : 0,
      lower: o.lower ? Math.round((s.peeked ? CARDS_PEEK_PAYS : pays(s, 'lower')) * 100) / 100 : 0,
      cardsLeft: s.deck.length,
      say: s.say,
      over: s.over,
    };
  },

  result(a, s): MinigameResult | null {
    if (!s.over) return null;
    const cash = s.over === 'cash';
    return {
      // Losing the stake scores no better than cashing it straight back.
      score: cash ? clamp(0.2 + (Math.log2(s.pot / a.stake) / 3) * 0.8, 0.2, 1) : s.over === 'caught' ? 0.1 : 0.2,
      title: cash ? `You walk away with ${s.pot} coin` : s.over === 'caught' ? 'Caught cheating!' : 'The card betrays you',
      text:
        s.over === 'caught' ? `You are thrown into the street, your ${a.stake} coin stake forfeit.`
        : s.over === 'lose' ? `You lose your ${a.stake} coin stake.`
        : `${s.streak} winning ${s.streak === 1 ? 'guess' : 'guesses'} at the ${a.name.toLowerCase()}.`,
      breakdown: [['Streak', s.streak / CARDS_MAX_STREAK], ['Suspicion', Math.min(1, s.suspicion / 100)]],
      yield: null,
      coin: cash ? s.pot : 0,
      xpMult: 1,
      feats: [...(s.streak >= CARDS_MAX_STREAK ? ['run-the-table'] : []), ...(s.over === 'caught' ? ['caught'] : [])],
    };
  },
};
